import { useEffect, useMemo } from "react";
import { useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import * as THREE from "three";
import type { StaticGltfPropSpec } from "../scenario/types";
import { markLoadDone, reportLoadProgress } from "./loadProgress";

const DEG = Math.PI / 180;

/**
 * A small static purchased/authored glTF prop (e.g. a scanned human figure)
 * dropped into the scene at a fixed transform. No animation/rig — purely a
 * decorative atmosphere prop layered on top of the main room mesh (which
 * itself loads via `GltfRoom`/`gltfUrl`, not this component).
 */
export function StaticGltfProp({ spec }: { spec: StaticGltfPropSpec }) {
  const gltf = useLoader(
    GLTFLoader,
    spec.url,
    (loader) => {
      (loader as GLTFLoader).setMeshoptDecoder(MeshoptDecoder);
    },
    (e) => reportLoadProgress(spec.url, e.loaded, e.lengthComputable ? e.total : 0),
  );

  const scene = useMemo(() => {
    const cloned = gltf.scene.clone(true);
    cloned.traverse((obj) => {
      obj.visible = true;
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        const mat = mesh.material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
        const mats = Array.isArray(mat) ? mat : [mat];
        for (const m of mats) {
          if (m && "envMapIntensity" in m) (m as THREE.MeshStandardMaterial).envMapIntensity = 0.6;
        }
      }
    });
    cloned.updateMatrixWorld(true);
    return cloned;
  }, [gltf]);

  useEffect(() => {
    markLoadDone(spec.url);
    window.dispatchEvent(new CustomEvent("cf-gltf-ready", { detail: { url: spec.url } }));
  }, [spec.url, scene]);

  const yaw = (spec.rotationDeg ?? 0) * DEG;
  return (
    <primitive
      object={scene}
      position={spec.position}
      rotation={[0, yaw, 0]}
      scale={spec.scale ?? 1}
    />
  );
}
